import axios from "./api";
import { ApiResponse } from "../types/user";
import { 
    Language, 
    Podcast, 
    PodcastSearchRequest, 
    PodcastSearchResponse,
    FilterPreset, 
    CreateFilterPresetRequest, 
    UpdateFilterPresetRequest,
    Episode,
    EpisodeSearchPodcastResult,
    EmailRevealUsage,
    EmailRevealLog
} from "../types/podcast";
import { PaginatedResponse } from "../types/common";

// Podcasts
export const searchPodcastsService = async (payload: PodcastSearchRequest) => {
    // Determine base URL: ensure we target /api/v2/podcasts/search
    // Use environment variable if available, otherwise construct from current origin or default
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
    const v2BaseUrl = apiBaseUrl.replace('/v1', '/v2');
    
    // Using simple axios.post with full URL to avoid instance interceptor issues with baseURL override
    const { data } = await axios.post<ApiResponse<PodcastSearchResponse>>(`${v2BaseUrl}/podcasts/search`, payload);
    return data;
};

export const searchPodcastsByEpisodeKeywordsService = async (keywords: string[], limitRecentEpisodes: number = 10, limit: number = 10) => {
    const { data } = await axios.get<ApiResponse<EpisodeSearchPodcastResult[]>>("/podcasts/search-by-episode-keywords", {
        params: { 
            keywords: keywords.join(','),
            limitRecentEpisodes,
            limit
        }
    });
    return data;
};

export const getPodcastByIdService = async (id: string) => {
    const { data } = await axios.get<ApiResponse<Podcast>>(`/podcasts/${id}`);
    return data;
};

export const getPodcastEpisodesService = async (id: string, limit: number = 10, page: number = 0) => {
    const { data } = await axios.get<ApiResponse<Episode[]>>(`/podcasts/${id}/episodes`, {
        params: { limit, page }
    });
    return data;
};

export const revealPodcastEmailService = async (id: string | number) => {
    const { data } = await axios.get<ApiResponse<{ email: string }>>(`/podcasts/${id}/reveal-email`);
    return data;
};

export const getEmailRevealUsageService = async () => {
    const { data } = await axios.get<ApiResponse<EmailRevealUsage>>("/podcasts/reveal-email/usage");
    return data;
};

export const getEmailRevealHistoryService = async (page: number = 0, size: number = 10, startDate?: string, endDate?: string) => {
    const { data } = await axios.get<ApiResponse<PaginatedResponse<EmailRevealLog>>>("/podcasts/reveal-email/history", {
        params: { page, size, startDate, endDate }
    });
    return data;
};

export const redeemCouponService = async (code: string) => {
    const { data } = await axios.post<ApiResponse<void>>("/coupons/redeem", { code });
    return data;
};

export const getLanguagesService = async () => {
    const { data } = await axios.get<ApiResponse<Language[]>>("/podcasts/languages");
    return data;
};

// Filter Presets
export const createFilterPresetService = async (payload: CreateFilterPresetRequest) => {
    const { data } = await axios.post<ApiResponse<FilterPreset>>("/podcasts/filter-presets", payload);
    return data;
};

export const getFilterPresetsService = async () => {
    const { data } = await axios.get<ApiResponse<FilterPreset[]>>("/podcasts/filter-presets");
    return data;
};

export const getFilterPresetByIdService = async (id: string) => {
    const { data } = await axios.get<ApiResponse<FilterPreset>>(`/podcasts/filter-presets/${id}`);
    return data;
};

export const updateFilterPresetService = async (id: string, payload: UpdateFilterPresetRequest) => {
    const { data } = await axios.put<ApiResponse<FilterPreset>>(`/podcasts/filter-presets/${id}`, payload);
    return data;
};

export const deleteFilterPresetService = async (id: string) => {
    const { data } = await axios.delete<ApiResponse>(`/podcasts/filter-presets/${id}`);
    return data;
};
